import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import {
  Star, Shield, ShieldCheck, Check, ChevronDown, ChevronUp,
  Droplets, Wind, Sun, Timer, Truck, RotateCcw, Leaf,
  ArrowRight, Minus, Plus, X
} from 'lucide-react';

const PRODUCT = {
  slug: 'daily-face-wash',
  name: 'Hard Water Defense Face Wash',
  brand: 'TRUFORM MEN',
  price: 249,
  originalPrice: 399,
  size: '100ml',
  rating: 4.8,
  reviewCount: 2547,
  tagline: 'Stops dryness caused by harsh Indian water.',
  description: 'pH 5.2 gel cleanser engineered for 300+ ppm hard water. Removes pollution particles, excess oil, and grime without stripping your moisture barrier. Designed for daily use in Indian conditions.',
};

const IMAGES = {
  packshot: 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/e891ab9ff3247b938261353d229048e69844615a4365660cffad83d87921d560.png',
  texture: 'https://images.unsplash.com/photo-1722002329028-b31ee16af8af?auto=format&fit=crop&w=800&q=80',
  application: 'https://images.unsplash.com/photo-1775642543443-a10792f9dc3d?auto=format&fit=crop&w=800&q=80',
  lifestyle: 'https://images.unsplash.com/photo-1598207950594-0fd066748f22?auto=format&fit=crop&w=800&q=80',
  products: 'https://images.unsplash.com/photo-1697201343045-64b1548535cb?auto=format&fit=crop&w=800&q=80',
  result1: 'https://images.unsplash.com/photo-1633116177630-dff41421deae?auto=format&fit=crop&w=600&q=80',
  result2: 'https://images.unsplash.com/photo-1699607426115-45d7fe0840d0?auto=format&fit=crop&w=600&q=80',
};

const GALLERY = [
  { src: IMAGES.packshot, label: 'Packshot' },
  { src: IMAGES.texture, label: 'Texture' },
  { src: IMAGES.application, label: 'In Use' },
  { src: IMAGES.lifestyle, label: 'Results' },
];

const REVIEWS = [
  { name: 'Rohan S.', city: 'Bangalore', rating: 5, text: "Finally a face wash that doesn't leave my skin feeling like sandpaper. Hard water at my place is terrible — this actually works.", verified: true, weeks: '2 weeks' },
  { name: 'Arjun M.', city: 'Mumbai', rating: 5, text: "Been using for a month. Zero dryness, even with Mumbai's water quality. My skin feels clean but not stripped.", verified: true, weeks: '4 weeks' },
  { name: 'Karan T.', city: 'Hyderabad', rating: 5, text: "Hyderabad water is the worst. This is the first face wash that doesn't make me reach for moisturiser immediately after.", verified: true, weeks: '3 weeks' },
  { name: 'Vikram P.', city: 'Delhi', rating: 4, text: "Pollution was making my skin dull. After 3 weeks, my skin looks noticeably cleaner and brighter. Subtle but real.", verified: true, weeks: '3 weeks' },
  { name: 'Aditya R.', city: 'Chennai', rating: 5, text: "Simple, effective, no drama. Lathers well, rinses clean, skin feels balanced. Exactly what I needed.", verified: true, weeks: '6 weeks' },
  { name: 'Siddharth N.', city: 'Pune', rating: 5, text: "Switched from a ₹600 imported brand. This works better for my skin and costs half. No brainer.", verified: true, weeks: '5 weeks' },
];

const INGREDIENTS = [
  { name: 'Niacinamide (3%)', benefit: 'Strengthens skin barrier, reduces pore appearance, evens tone', highlight: true },
  { name: 'Salicylic Acid (0.5%)', benefit: 'Gently unclogs pores, prevents breakouts without over-drying', highlight: true },
  { name: 'Aloe Vera Extract', benefit: 'Soothes irritation from hard water minerals', highlight: false },
  { name: 'Glycerin', benefit: 'Locks in moisture, prevents post-wash tightness', highlight: false },
  { name: 'Tea Tree Oil', benefit: 'Natural antibacterial, controls excess sebum', highlight: false },
  { name: 'Vitamin E', benefit: 'Antioxidant protection against pollution damage', highlight: false },
];

const FAQ_DATA = [
  { q: 'Will this work with my hard water?', a: 'Yes. This is specifically formulated for water hardness levels of 300+ ppm, which covers most Indian cities including Delhi, Hyderabad, Bangalore, and Chennai. The pH 5.2 formula counteracts the alkalinity of hard water.' },
  { q: 'Is it safe for daily use?', a: 'Absolutely. Designed for twice-daily use (morning and night). The gentle, sulphate-free formula won\'t strip your skin even with consistent use. Dermatologically tested for Indian skin types.' },
  { q: 'Will it help with acne?', a: 'The 0.5% Salicylic Acid helps prevent breakouts by gently unclogging pores. For active acne, pair with our Pro Active Face Wash which has higher concentrations of active ingredients.' },
  { q: 'How is this different from regular face washes?', a: 'Regular face washes assume clean, soft water. Indian water has 3-5x more minerals that leave residue on your skin. Our formula is designed to work with (not against) hard water, preventing the dryness and irritation most men experience.' },
  { q: 'What\'s the return policy?', a: '7-day hassle-free returns. If it doesn\'t work for your skin, we\'ll refund you fully. No questions asked.' },
  { q: 'How long before I see results?', a: 'Most customers notice reduced dryness and tightness from the first wash. Visible texture improvement typically happens in 1-2 weeks. Full benefits (even tone, clearer skin) within 3-4 weeks.' },
];

export default function HardWaterFacewashPage() {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const ctaRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (ctaRef.current) {
        const rect = ctaRef.current.getBoundingClientRect();
        setShowSticky(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAdd = () => {
    addItem({
      id: PRODUCT.slug,
      name: PRODUCT.name,
      price: PRODUCT.price,
      size: PRODUCT.size,
      image: IMAGES.packshot,
      quantity: qty,
    });
  };

  const handleBuyNow = () => {
    handleAdd();
    navigate('/checkout');
  };

  return (
    <div className="bg-[#F0F9FF]">
      {/* ═══════════ SECTION 1: ABOVE THE FOLD ═══════════ */}
      <AboveTheFold
        activeImg={activeImg}
        setActiveImg={setActiveImg}
        qty={qty}
        setQty={setQty}
        onAdd={handleAdd}
        onBuyNow={handleBuyNow}
        ctaRef={ctaRef}
      />

      {/* ═══════════ SECTION 2: WHY THIS WORKS ═══════════ */}
      <WhyThisWorks />

      {/* ═══════════ SECTION 3: HOW TO USE ═══════════ */}
      <HowToUse />

      {/* ═══════════ SECTION 4: BEFORE / AFTER ═══════════ */}
      <BeforeAfter />

      {/* ═══════════ SECTION 5: SOCIAL PROOF ═══════════ */}
      <SocialProof />

      {/* ═══════════ SECTION 6: INGREDIENTS ═══════════ */}
      <IngredientsSection />

      {/* ═══════════ SECTION 7: FAQ ═══════════ */}
      <FAQSection />

      {/* ═══════════ FINAL CTA ═══════════ */}
      <FinalCTA onAdd={handleAdd} />

      {/* ═══════════ STICKY MOBILE CTA ═══════════ */}
      <div
        data-testid="sticky-cta"
        className={`fixed bottom-0 left-0 right-0 z-50 bg-[#0C4A8C] border-t px-4 py-3 md:hidden transition-transform duration-300 ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ borderColor: 'rgba(186,230,253,0.15)' }}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-white text-[13px] font-serif font-medium">{PRODUCT.name}</p>
            <div className="flex items-center gap-2">
              <span className="text-white font-serif text-[15px]">&#8377;{PRODUCT.price}</span>
              <span className="text-white/30 text-[12px] line-through">&#8377;{PRODUCT.originalPrice}</span>
            </div>
          </div>
          <button
            data-testid="sticky-add-to-cart"
            onClick={handleAdd}
            className="bg-[#F0F9FF] text-[#0C4A8C] px-5 py-2.5 rounded-[2px] text-[10px] font-sans font-semibold uppercase tracking-[2px] hover:bg-white transition-colors whitespace-nowrap"
          >
            Fix My Skin
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── ABOVE THE FOLD ─────────── */
function AboveTheFold({ activeImg, setActiveImg, qty, setQty, onAdd, onBuyNow, ctaRef }) {
  return (
    <section data-testid="above-the-fold" className="bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
          {/* LEFT — Image Gallery */}
          <div data-testid="product-gallery">
            <div className="aspect-square rounded-[3px] overflow-hidden bg-[#F0F9FF] mb-3">
              <img
                src={GALLERY[activeImg].src}
                alt={GALLERY[activeImg].label}
                data-testid="product-main-image"
                className="w-full h-full object-cover transition-opacity duration-300"
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {GALLERY.map((img, i) => (
                <button
                  key={img.label}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square rounded-[2px] overflow-hidden transition-all duration-200 ${activeImg === i ? 'ring-2 ring-[#0C4A8C] opacity-100' : 'opacity-50 hover:opacity-80'}`}
                >
                  <img src={img.src} alt={img.label} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT — Product Info */}
          <div data-testid="product-info" className="flex flex-col">
            {/* Breadcrumb */}
            <p className="text-[10px] font-sans text-[#6B7D8A] uppercase tracking-[2px] mb-3">
              <Link to="/shop" className="hover:text-[#0C4A8C] transition-colors">Shop</Link>
              <span className="mx-2">/</span>
              <Link to="/shop?category=face" className="hover:text-[#0C4A8C] transition-colors">Face Care</Link>
              <span className="mx-2">/</span>
              <span className="text-[#0C4A8C]">Hard Water Defense</span>
            </p>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4" data-testid="product-rating">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={14} className={s <= Math.round(PRODUCT.rating) ? 'text-[#0C7BC0] fill-[#0C7BC0]' : 'text-[#CBD5E1]'} />
                ))}
              </div>
              <span className="text-[12px] font-sans text-[#0C4A8C] font-medium">{PRODUCT.rating}</span>
              <span className="text-[11px] font-sans text-[#6B7D8A]">({PRODUCT.reviewCount.toLocaleString()} reviews)</span>
            </div>

            {/* Name */}
            <h1 data-testid="product-name" className="font-serif text-[28px] md:text-[34px] font-medium text-[#0C4A8C] leading-tight mb-2">
              {PRODUCT.name}
            </h1>

            {/* Tagline */}
            <p data-testid="product-tagline" className="text-base md:text-lg font-serif italic text-[#6B7D8A] mb-5">
              {PRODUCT.tagline}
            </p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6" data-testid="product-price">
              <span className="font-serif text-[28px] text-[#0C4A8C] font-bold">&#8377;{PRODUCT.price}</span>
              <span className="font-sans text-[16px] text-[#6B7D8A] line-through">&#8377;{PRODUCT.originalPrice}</span>
              <span className="text-[11px] font-sans font-semibold uppercase tracking-[1px] text-white bg-[#0C7BC0] px-2 py-0.5 rounded-[2px]">
                {Math.round((1 - PRODUCT.price / PRODUCT.originalPrice) * 100)}% OFF
              </span>
            </div>

            {/* Benefit bullets */}
            <div className="space-y-3 mb-7" data-testid="benefit-bullets">
              {[
                { icon: Droplets, text: 'Prevents dryness from hard water (300+ ppm)' },
                { icon: Wind, text: 'Removes dirt, pollution & PM2.5 particles' },
                { icon: Shield, text: 'Gentle daily cleanser — won\'t strip your skin' },
                { icon: Leaf, text: 'pH 5.2 balanced for Indian conditions' },
              ].map((b, i) => (
                <div key={b.text} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#0C4A8C] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <b.icon size={13} className="text-[#BAE6FD]" />
                  </div>
                  <span className="text-[14px] text-[#3D4F5C] font-light leading-relaxed">{b.text}</span>
                </div>
              ))}
            </div>

            {/* Quantity + CTA */}
            <div ref={ctaRef} className="space-y-3 mb-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center border rounded-[2px]" style={{ borderColor: 'rgba(12,74,140,0.12)' }}>
                  <button data-testid="qty-decrease" onClick={() => setQty(q => Math.max(1, q - 1))} className="px-3 py-2.5 text-[#6B7D8A] hover:text-[#0C4A8C] transition-colors">
                    <Minus size={14} />
                  </button>
                  <span data-testid="qty-value" className="px-3 py-2.5 text-[14px] font-sans font-medium text-[#0C4A8C] min-w-[32px] text-center">{qty}</span>
                  <button data-testid="qty-increase" onClick={() => setQty(q => q + 1)} className="px-3 py-2.5 text-[#6B7D8A] hover:text-[#0C4A8C] transition-colors">
                    <Plus size={14} />
                  </button>
                </div>
                <span className="text-[12px] font-sans text-[#6B7D8A]">{PRODUCT.size} per unit</span>
              </div>

              <button
                data-testid="fix-my-skin-btn"
                onClick={onAdd}
                className="w-full bg-[#0C4A8C] text-[#F0F9FF] py-4 rounded-[2px] text-[12px] font-sans font-semibold uppercase tracking-[3px] hover:bg-[#093B70] transition-all duration-200 active:scale-[0.98]"
              >
                Fix My Skin — &#8377;{PRODUCT.price * qty}
              </button>

              <button
                data-testid="buy-now-btn"
                onClick={onBuyNow}
                className="w-full border-2 border-[#0C4A8C] text-[#0C4A8C] py-3.5 rounded-[2px] text-[11px] font-sans font-semibold uppercase tracking-[3px] hover:bg-[#0C4A8C] hover:text-[#F0F9FF] transition-all duration-200"
              >
                Buy Now
              </button>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-3" data-testid="trust-badges">
              {[
                { icon: ShieldCheck, text: 'Dermatologically Tested' },
                { icon: Timer, text: 'Safe for Daily Use' },
                { icon: Truck, text: 'Free Ship ₹499+' },
              ].map((b, i) => (
                <div key={b.text} className="flex flex-col items-center text-center p-3 rounded-[2px] bg-[#F0F9FF]">
                  <b.icon size={18} className="text-[#0C7BC0] mb-1.5" />
                  <span className="text-[9px] font-sans text-[#3D4F5C] uppercase tracking-[1px] leading-tight">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── SECTION 2: WHY THIS WORKS ─────────── */
function WhyThisWorks() {
  return (
    <section data-testid="why-this-works" className="py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="eyebrow text-[#0C7BC0] mb-4">The Problem</p>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#0C4A8C] tracking-tight mb-4 leading-tight">
            Why regular face washes fail in India.
          </h2>
          <p className="text-sm text-[#6B7D8A] font-light max-w-lg mx-auto">
            Most face washes are formulated for soft European water. Indian water is 3-5x harder — and that changes everything.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-[3px] overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(12,74,140,0.06)' }} data-testid="comparison-table">
          {/* Header */}
          <div className="grid grid-cols-3">
            <div className="p-5 md:p-6 bg-[#F0F9FF]">
              <span className="text-[10px] font-sans font-medium uppercase tracking-[2px] text-[#6B7D8A]">Feature</span>
            </div>
            <div className="p-5 md:p-6 bg-[#F0F9FF] text-center">
              <span className="text-[10px] font-sans font-medium uppercase tracking-[2px] text-[#6B7D8A]">Regular Face Wash</span>
            </div>
            <div className="p-5 md:p-6 bg-[#0C4A8C] text-center">
              <span className="text-[10px] font-sans font-medium uppercase tracking-[2px] text-[#BAE6FD]">TRUFORM Defense</span>
            </div>
          </div>
          {/* Rows */}
          {[
            { feature: 'Hard Water Protection', regular: 'None', truform: 'pH 5.2 balanced' },
            { feature: 'Post-Wash Feeling', regular: 'Tight & dry', truform: 'Clean & hydrated' },
            { feature: 'Pollution Removal', regular: 'Surface only', truform: 'Deep particulate removal' },
            { feature: 'Skin Barrier', regular: 'Strips oils', truform: 'Preserves barrier' },
            { feature: 'Made For', regular: 'Western conditions', truform: 'Indian water & climate' },
            { feature: 'SLS / Sulphates', regular: 'Yes', truform: 'Zero sulphates' },
          ].map((row, i) => (
            <div key={row.feature || i} className="grid grid-cols-3" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
              <div className="p-4 md:p-5">
                <span className="text-[13px] font-sans text-[#0C4A8C] font-medium">{row.feature}</span>
              </div>
              <div className="p-4 md:p-5 text-center flex items-center justify-center">
                <span className="text-[12px] font-sans text-[#6B7D8A]">{row.regular === 'None' || row.regular === 'Yes' || row.regular.includes('Strips') || row.regular.includes('Western') || row.regular.includes('Surface') || row.regular.includes('Tight')
                  ? <span className="flex items-center justify-center gap-1.5"><X size={13} className="text-red-400" />{row.regular}</span>
                  : row.regular
                }</span>
              </div>
              <div className="p-4 md:p-5 bg-[#0C4A8C]/[0.03] text-center flex items-center justify-center">
                <span className="text-[12px] font-sans text-[#0C4A8C] font-medium flex items-center justify-center gap-1.5">
                  <Check size={13} className="text-[#0C7BC0]" />{row.truform}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── SECTION 3: HOW TO USE ─────────── */
function HowToUse() {
  return (
    <section data-testid="how-to-use" className="bg-white py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="eyebrow text-[#0C7BC0] mb-4">How to Use</p>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#0C4A8C] tracking-tight mb-4">3 steps. 60 seconds. Done.</h2>
          <p className="text-sm text-[#6B7D8A] font-light max-w-md mx-auto">No complicated routine. Just clean, protected skin.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { num: '01', title: 'Wet', desc: 'Splash face with water. Works with any water quality — hard, soft, or anywhere in between.', time: '5 sec' },
            { num: '02', title: 'Apply', desc: 'Take a coin-sized amount. Massage in circular motions for 30 seconds. Focus on T-zone and jawline.', time: '30 sec' },
            { num: '03', title: 'Rinse', desc: 'Rinse thoroughly. Pat dry with a clean towel. Your skin should feel clean, never tight or dry.', time: '15 sec' },
          ].map((step, i) => (
            <div key={step.title || i} data-testid={`how-to-step-${i}`} className="relative text-center p-8 rounded-[3px]" style={{ background: '#F0F9FF' }}>
              <span className="absolute top-4 left-5 font-serif text-[56px] font-bold leading-none" style={{ color: 'rgba(12,74,140,0.04)' }}>{step.num}</span>
              <div className="w-16 h-16 rounded-full bg-[#0C4A8C] text-[#BAE6FD] flex items-center justify-center mx-auto mb-5 relative z-10">
                <span className="font-serif text-[22px] font-bold text-white">{i + 1}</span>
              </div>
              <h3 className="font-serif text-2xl text-[#0C4A8C] font-medium mb-2">{step.title}</h3>
              <div className="flex items-center justify-center gap-1.5 mb-4">
                <Timer size={12} className="text-[#0C7BC0]" />
                <span className="text-[10px] font-sans font-medium text-[#0C7BC0] uppercase tracking-[2px]">{step.time}</span>
              </div>
              <p className="text-[13px] text-[#6B7D8A] font-light leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-6">
          <p className="text-[12px] font-sans text-[#6B7D8A] italic">Use twice daily — morning and night — for best results.</p>
        </div>
      </div>
    </section>
  );
}

/* ─────────── SECTION 4: BEFORE / AFTER ─────────── */
function BeforeAfter() {
  return (
    <section data-testid="before-after" className="py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="eyebrow text-[#0C7BC0] mb-4">Real Results</p>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#0C4A8C] tracking-tight mb-4">What to actually expect.</h2>
          <p className="text-sm text-[#6B7D8A] font-light max-w-lg mx-auto">No fake before-afters. Just honest timelines from real users in real Indian cities.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Timeline Card 1 */}
          <div className="bg-white rounded-[3px] overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(12,74,140,0.06)' }}>
            <div className="aspect-[16/10] overflow-hidden">
              <img src={IMAGES.result1} alt="Skin improvement results" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <p className="text-[10px] font-sans font-semibold text-[#0C7BC0] uppercase tracking-[2px] mb-2">Week 1-2</p>
              <h3 className="font-serif text-lg text-[#0C4A8C] font-medium mb-2">Dryness stops. Skin feels balanced.</h3>
              <p className="text-[13px] text-[#6B7D8A] font-light leading-relaxed">Hard water damage starts reversing. Post-wash tightness disappears. Skin retains natural moisture.</p>
              <div className="flex items-end gap-2 mt-4 pt-4" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
                <span className="font-serif text-[32px] text-[#0C4A8C] font-bold leading-none">87%</span>
                <span className="text-[11px] text-[#6B7D8A] font-sans pb-1">noticed softer skin</span>
              </div>
            </div>
          </div>

          {/* Timeline Card 2 */}
          <div className="bg-white rounded-[3px] overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(12,74,140,0.06)' }}>
            <div className="aspect-[16/10] overflow-hidden">
              <img src={IMAGES.result2} alt="Clear skin results" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <p className="text-[10px] font-sans font-semibold text-[#0C7BC0] uppercase tracking-[2px] mb-2">Week 3-4</p>
              <h3 className="font-serif text-lg text-[#0C4A8C] font-medium mb-2">Clearer texture. Visible improvement.</h3>
              <p className="text-[13px] text-[#6B7D8A] font-light leading-relaxed">Pores unclog. Pollution dullness fades. Skin looks cleaner and more even-toned overall.</p>
              <div className="flex items-end gap-2 mt-4 pt-4" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
                <span className="font-serif text-[32px] text-[#0C4A8C] font-bold leading-none">91%</span>
                <span className="text-[11px] text-[#6B7D8A] font-sans pb-1">recommend to others</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { val: '2,500+', label: 'Happy customers' },
            { val: '4.8/5', label: 'Average rating' },
            { val: '73%', label: 'Saw fewer breakouts' },
          ].map((s, i) => (
            <div key={s.label || i} className="text-center p-5 bg-white rounded-[3px]" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.04)' }}>
              <p className="font-serif text-[24px] md:text-[28px] text-[#0C4A8C] font-bold leading-none">{s.val}</p>
              <p className="text-[10px] font-sans text-[#6B7D8A] mt-1 uppercase tracking-[1px]">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── SECTION 5: SOCIAL PROOF ─────────── */
function SocialProof() {
  return (
    <section data-testid="social-proof" className="bg-[#0C4A8C] py-20 md:py-28 px-4 sm:px-6 md:px-10 relative grain-overlay">
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14">
          <div>
            <p className="eyebrow text-[#BAE6FD] mb-4">Reviews</p>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-white tracking-tight">What Indian men are saying.</h2>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-3">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map(s => <Star key={s} size={14} className="text-[#0C7BC0] fill-[#0C7BC0]" />)}
            </div>
            <div>
              <p className="text-[14px] font-serif text-white font-medium">{PRODUCT.rating}/5</p>
              <p className="text-[10px] font-sans text-white/35">{PRODUCT.reviewCount.toLocaleString()} reviews</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REVIEWS.map((r, i) => (
            <div key={r.name} data-testid={`review-${i}`} className="p-5 rounded-[3px]" style={{ background: 'rgba(186,230,253,0.06)', border: '1px solid rgba(186,230,253,0.1)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} size={11} className={s <= r.rating ? 'text-[#0C7BC0] fill-[#0C7BC0]' : 'text-white/15'} />
                  ))}
                </div>
                {r.verified && (
                  <span className="text-[8px] font-sans uppercase tracking-[1.5px] text-[#BAE6FD] flex items-center gap-1">
                    <Check size={10} /> Verified
                  </span>
                )}
              </div>
              <p className="text-[13px] text-white/60 font-serif italic leading-relaxed mb-4">"{r.text}"</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-[4px] bg-white text-[#0C4A8C] flex items-center justify-center text-[12px] font-serif font-bold leading-none">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-[11px] text-white/80 font-sans font-medium">{r.name}</p>
                    <p className="text-[9px] text-white/30 font-sans">{r.city}</p>
                  </div>
                </div>
                <span className="text-[9px] font-sans text-white/20 uppercase tracking-[1px]">{r.weeks}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── SECTION 6: INGREDIENTS ─────────── */
function IngredientsSection() {
  return (
    <section data-testid="ingredients-section" className="bg-white py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <p className="eyebrow text-[#0C7BC0] mb-4">Ingredients</p>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#0C4A8C] tracking-tight mb-4 leading-tight">Every ingredient.<br />A reason it's there.</h2>
            <p className="text-sm text-[#6B7D8A] font-light mb-8 max-w-md">No fillers. No mystery compounds. Every ingredient has a specific purpose for Indian skin conditions.</p>

            <div className="aspect-[4/3] rounded-[3px] overflow-hidden">
              <img src={IMAGES.products} alt="TRUFORM products" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="space-y-0">
            {INGREDIENTS.map((ing, i) => (
              <div key={ing.name} data-testid={`ingredient-${i}`} className="flex gap-4 items-start py-5" style={{ borderBottom: i < INGREDIENTS.length - 1 ? '1px solid rgba(12,74,140,0.06)' : 'none' }}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${ing.highlight ? 'bg-[#0C4A8C]' : 'bg-[#F0F9FF]'}`}>
                  <Check size={14} className={ing.highlight ? 'text-[#BAE6FD]' : 'text-[#6B7D8A]'} />
                </div>
                <div>
                  <h4 className="text-[14px] font-sans font-medium text-[#0C4A8C] mb-0.5">{ing.name}</h4>
                  <p className="text-[13px] text-[#6B7D8A] font-light leading-relaxed">{ing.benefit}</p>
                </div>
              </div>
            ))}

            <div className="pt-5 mt-2 flex items-center gap-3" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
              <Leaf size={16} className="text-[#0C7BC0]" />
              <p className="text-[11px] font-sans text-[#6B7D8A]">
                <span className="font-medium text-[#0C4A8C]">Zero</span> parabens, SLS, synthetic fragrance, mineral oil, or artificial colors.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── SECTION 7: FAQ ─────────── */
function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section data-testid="faq-section" className="py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <p className="eyebrow text-[#0C7BC0] mb-4">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#0C4A8C] tracking-tight mb-4">Common questions.</h2>
        </div>

        <div className="space-y-0">
          {FAQ_DATA.map((faq, i) => (
            <div key={faq.q} data-testid={`faq-item-${i}`} style={{ borderBottom: '1px solid rgba(12,74,140,0.08)' }}>
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left"
              >
                <span className="font-serif text-[16px] md:text-[17px] text-[#0C4A8C] font-medium pr-4">{faq.q}</span>
                {openIdx === i
                  ? <ChevronUp size={18} className="text-[#6B7D8A] flex-shrink-0" />
                  : <ChevronDown size={18} className="text-[#6B7D8A] flex-shrink-0" />
                }
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIdx === i ? 'max-h-60 pb-5' : 'max-h-0'}`}>
                <p className="text-[13px] text-[#6B7D8A] font-light leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── FINAL CTA ─────────── */
function FinalCTA({ onAdd }) {
  return (
    <section data-testid="final-cta" className="bg-[#0C4A8C] relative grain-overlay py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-2xl mx-auto text-center relative z-10">
        <h2 className="text-3xl md:text-4xl font-serif font-medium text-white tracking-tight mb-4 leading-tight">
          Stop fighting your water.<br />Fix your face wash.
        </h2>
        <p className="text-sm font-light mb-8 text-white/35">
          Join 2,500+ men who switched to the only face wash built for Indian water.
        </p>
        <button
          data-testid="final-cta-btn"
          onClick={onAdd}
          className="bg-[#F0F9FF] text-[#0C4A8C] px-10 py-4 rounded-[2px] text-[12px] font-sans font-semibold uppercase tracking-[3px] hover:bg-white transition-colors"
        >
          Fix My Skin — &#8377;{PRODUCT.price}
        </button>
        <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
          {[
            { icon: Truck, text: 'Free shipping ₹499+' },
            { icon: RotateCcw, text: '7-day returns' },
            { icon: ShieldCheck, text: 'Dermat tested' },
          ].map((b, i) => (
            <div key={b.text} className="flex items-center gap-1.5">
              <b.icon size={13} className="text-[#BAE6FD]" />
              <span className="text-[10px] font-sans text-white/30 uppercase tracking-[1px]">{b.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
